import { useState, useEffect, type RefObject } from 'react';

/**
 * Collapse levels for TopNavigation responsive behavior.
 * As the nav container shrinks, items collapse in priority order:
 *
 * 0 — Full layout, nothing collapsed
 * 1 — Search bar → icon-only
 * 2 — Logo → icon only
 * 3 — User menu → avatar only (no text/chevron)
 * 4 — Help + Notification hidden
 * 5 — Engage + Analytics → "More" dropdown
 * 6 — OptiDev → "More" dropdown
 */

const COLLAPSE_THRESHOLDS = [
  1400, // level 1: search → icon
  1300, // level 2: logo → icon
  1200, // level 3: user menu → icon
  1100, // level 4: hide help + notifications
  1000, // level 5: engage + analytics → more
  950, // level 6: optidev → more
] as const;

export function useNavBreakpoints(ref: RefObject<HTMLElement | null>): number {
  const [collapseLevel, setCollapseLevel] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    function calcLevel(width: number): number {
      let level = 0;
      for (const threshold of COLLAPSE_THRESHOLDS) {
        if (width < threshold) {
          level++;
        } else {
          break;
        }
      }
      return level;
    }

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        setCollapseLevel(calcLevel(entry.contentRect.width));
      }
    });

    observer.observe(el);
    setCollapseLevel(calcLevel(el.clientWidth));

    return () => observer.disconnect();
  }, [ref]);

  return collapseLevel;
}
